import { DescriptionBox } from "@jugl-web/domain-resources/common/components/DescriptionBox";
import {
  NavigationGuardProvider,
  useNavigationGuardContext,
} from "@jugl-web/domain-resources/common/providers/NavigationGuardProvider";
import {
  MAX_TICKET_DESCRIPTION_LENGTH,
  MAX_TICKET_TITLE_LENGTH,
} from "@jugl-web/domain-resources/tickets/consts";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { CreateTicketPayload } from "@jugl-web/rest-api/tickets/types";
import { Button, TitleBox } from "@jugl-web/ui-kit";
import { isAPIError, useToast } from "@jugl-web/utils";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, useCallback, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { NewTicketFormValues } from "../../../../types";
import { AssigneesDetailItem } from "../../../AssigneesDetailItem";
import { ContactDetailItem } from "../../../ContactDetailItem";
import { DueToDetailItem } from "../../../DueToDetailItem";
import { PriorityDetailItem } from "../../../PriorityDetailItem";
import { StatusDetailItem } from "../../../StatusDetailItem";
import { TicketSidebarHeader } from "../../../TicketSidebarHeader";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";

const DEFAULT_FORM_VALUES: NewTicketFormValues = {
  title: "",
  desc: "",
  status: "in_progress",
  priority: "none",
  contact: null,
  dueAt: null,
  assigneeIds: [],
};

const toCreateTicketPayload = (
  formValues: NewTicketFormValues
): CreateTicketPayload => {
  if (!formValues.contact) {
    throw new Error("Contact is required");
  }

  return {
    title: formValues.title.trim(),
    desc: formValues.desc.trim(),
    status: formValues.status,
    priority: formValues.priority,
    contact_id: formValues.contact.id,
    due_at: formValues.dueAt ? formValues.dueAt.toISOString() : null,
    assignees: formValues.assigneeIds,
  };
};

const CREATE_TICKET_FORM_DIRTY_SOURCE_ID = "create-ticket-sidebar-form";

interface CreateTicketSidebarContentProps {
  initialFormValues?: Partial<NewTicketFormValues>;
  onClose: () => void;
  registerCustomCloseHandler?: (handler: (() => void) | null) => void;
}

const CreateTicketSidebarContentInner: FC<CreateTicketSidebarContentProps> = ({
  initialFormValues,
  onClose,
  registerCustomCloseHandler,
}) => {
  const { entityId } = useEntitySelectedProvider();

  const { ticketsApi } = useRestApiProvider();
  const { ticketMutations$, openTicketDetailsSidebar } =
    useClientSupportPageContext();

  const { registerDirtySource, requestNavigation } =
    useNavigationGuardContext();

  const [createTicketMutation, { isLoading }] =
    ticketsApi.useCreateTicketMutation();

  const form = useForm<NewTicketFormValues>({
    defaultValues: {
      ...DEFAULT_FORM_VALUES,
      ...initialFormValues,
    },
  });

  const status = form.watch("status");

  const { toast, interactiveToast } = useToast();

  const handleSafeClose = useCallback(() => {
    requestNavigation(() => {
      form.reset();
      onClose();
    });
  }, [form, onClose, requestNavigation]);

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      const createdTicket = await createTicketMutation({
        entityId,
        data: toCreateTicketPayload(values),
      }).unwrap();

      ticketMutations$.next({ type: "created", ticket: createdTicket });
      interactiveToast("Ticket has been created. Tap to open it", () =>
        openTicketDetailsSidebar(createdTicket.id)
      );
      form.reset();
      onClose();
    } catch (e) {
      // Skip error if ticket limit is exceeded (we have another toast showing up already)
      if (isAPIError(e) && e.data.errors === "ticket_limit_exceeded") {
        return;
      }

      toast("Failed to create ticket", { variant: "error" });
    }
  });

  // Register the form as a dirty source so that the navigation guard can block navigation
  useEffect(() => {
    registerDirtySource(
      CREATE_TICKET_FORM_DIRTY_SOURCE_ID,
      form.formState.isDirty
    );
  }, [form.formState.isDirty, registerDirtySource]);

  // Register the close handler in the parent component so we can also intercept outside clicks/escapes
  useEffect(() => {
    registerCustomCloseHandler?.(handleSafeClose);

    return () => {
      registerCustomCloseHandler?.(null);
    };
  }, [handleSafeClose, registerCustomCloseHandler]);

  return (
    <form className="flex h-full flex-col" onSubmit={handleSubmit}>
      <TicketSidebarHeader title="New Ticket" onClose={handleSafeClose} />
      <div className="jugl__custom-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="px-8 pt-8 pb-6">
          <Controller
            control={form.control}
            name="title"
            rules={{
              validate: (value) =>
                value.trim().length > 0 || "Enter topic to proceed",
            }}
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-3">
                <TitleBox
                  isEditable
                  title={field.value}
                  placeholder="Enter ticket topic"
                  emptyPlaceholder="(no topic)"
                  maxLength={MAX_TICKET_TITLE_LENGTH}
                  onInternalValueChange={field.onChange}
                  validator={(title) => title.trim().length > 0}
                />
                {fieldState.error?.message && (
                  <span className="text-sm leading-[150%] font-normal text-tertiary-700">
                    {fieldState.error?.message}
                  </span>
                )}
              </div>
            )}
          />
        </div>

        <Separator />

        <div className="flex flex-col gap-2 p-8">
          {/* Contact */}
          <Controller
            control={form.control}
            name="contact"
            rules={{
              validate: (value) =>
                value !== null || "Select customer to proceed",
            }}
            render={({ field, fieldState }) => (
              <ContactDetailItem
                contact={field.value}
                onChange={field.onChange}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
          {/* Assignees */}
          <Controller
            control={form.control}
            name="assigneeIds"
            render={({ field }) => (
              <AssigneesDetailItem
                assigneeIds={field.value}
                onChange={field.onChange}
              />
            )}
          />
          {/* Status */}
          <Controller
            control={form.control}
            name="status"
            render={({ field }) => (
              <StatusDetailItem
                status={field.value}
                onChange={field.onChange}
              />
            )}
          />
          {/* Priority */}
          <Controller
            control={form.control}
            name="priority"
            render={({ field }) => (
              <PriorityDetailItem
                priority={field.value}
                onChange={field.onChange}
              />
            )}
          />
          {/* Due To */}
          <Controller
            control={form.control}
            name="dueAt"
            render={({ field }) => (
              <DueToDetailItem
                dueDate={field.value}
                status={status}
                onChange={field.onChange}
              />
            )}
          />
        </div>

        <Separator />

        <div className="p-8">
          {/* Description */}
          <div className="mb-4 flex flex-col gap-2 px-3">
            <span className="font-primary text-xs leading-[1.4] tracking-[0.12px] text-dark-700">
              Description:
            </span>
            <Controller
              control={form.control}
              name="desc"
              render={({ field }) => (
                <DescriptionBox
                  isEditable
                  description={field.value}
                  classes={{ descriptionText: "py-2" }}
                  onChange={field.onChange}
                  maxTextLength={MAX_TICKET_DESCRIPTION_LENGTH}
                />
              )}
            />
          </div>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-3 border-t border-solid border-dark-100 px-8 py-6">
        <Button
          type="button"
          variant="tertiary"
          onClick={handleSafeClose}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          iconEnd={<ArrowRightIcon />}
          isDisabled={isLoading}
          isLoading={isLoading}
          className="flex-2"
        >
          Create
        </Button>
      </div>
    </form>
  );
};

const Separator = () => <div className="h-px w-full bg-grey-200" />;

export const CreateTicketSidebarContent: FC<CreateTicketSidebarContentProps> = (
  props
) => (
  <NavigationGuardProvider>
    <CreateTicketSidebarContentInner {...props} />
  </NavigationGuardProvider>
);
